import type { FinancialSnapshot } from './types';

export interface MonthlyIncome { id: string; name: string; half: 1|2; amountCents: number|null; registeredInFunds: boolean }
export type MonthlyGroup = 'fixed' | 'credit' | 'other';
export interface MonthlyEntry { id: string; name: string; note: string; group: MonthlyGroup; amountCents: number | null; settled?: boolean }
export interface MonthlyPlan {
  schemaVersion: 1; month: string; revision: number; updatedAt: string;
  incomeQ1Cents: number | null; incomeQ2Cents: number | null;
  allocationQ1Cents: number | null; allocationQ2Cents: number | null;
  entries: MonthlyEntry[];
  incomes?: MonthlyIncome[];
}
export const validMonth = (value: string) => /^20\d{2}-(0[1-9]|1[0-2])$/.test(value);
const validMoney = (value: unknown) => value === null || (Number.isSafeInteger(value) && Number(value) >= 0 && Number(value) <= 100_000_000);
export function validateMonthlyPlan(plan: MonthlyPlan): MonthlyPlan {
  if (!plan || plan.schemaVersion !== 1 || !validMonth(plan.month) || !Number.isSafeInteger(plan.revision) || plan.revision < 0 || typeof plan.updatedAt !== 'string') throw new Error('La planeación mensual no es válida.');
  if (![plan.incomeQ1Cents,plan.incomeQ2Cents,plan.allocationQ1Cents,plan.allocationQ2Cents].every(validMoney)) throw new Error('Revisa los importes de la planeación.');
  if (!Array.isArray(plan.entries) || plan.entries.length > 30) throw new Error('Puedes conservar hasta 30 conceptos por mes.');
  const ids = new Set<string>();
  for (const entry of plan.entries) {
    if (!/^[a-zA-Z0-9_-]{1,80}$/.test(entry.id) || ids.has(entry.id) || typeof entry.name !== 'string' || !entry.name.trim() || entry.name.length > 100 || typeof entry.note !== 'string' || entry.note.length > 240 || !['fixed','credit','other'].includes(entry.group) || !validMoney(entry.amountCents) || (entry.settled !== undefined && typeof entry.settled !== 'boolean')) throw new Error('Revisa los conceptos de la planeación.');
    ids.add(entry.id);
  }
  if (plan.incomes !== undefined) {
    if (!Array.isArray(plan.incomes) || plan.incomes.length > 20) throw new Error('Puedes conservar hasta 20 ingresos por mes.');
    const incomeIds = new Set<string>();
    for (const row of plan.incomes) {
      if (!row || !/^[a-zA-Z0-9_-]{1,80}$/.test(row.id) || incomeIds.has(row.id) || typeof row.name !== 'string' || !row.name.trim() || row.name.length > 100 || ![1,2].includes(row.half) || !validMoney(row.amountCents) || typeof row.registeredInFunds !== 'boolean') throw new Error('Revisa los ingresos de la planeación.');
      incomeIds.add(row.id);
    }
    for (const half of [1,2] as const) {
      const rows = plan.incomes.filter(row=>row.half===half);
      const known = rows.reduce((sum,row)=>sum+(row.amountCents??0),0);
      const total = rows.some(row=>row.amountCents===null)?null:known;
      if (known>100_000_000 || total !== (half===1?plan.incomeQ1Cents:plan.incomeQ2Cents)) throw new Error('El total de ingresos no coincide con su desglose.');
    }
  }
  return plan;
}
export function emptyMonthlyPlan(month: string): MonthlyPlan {
  return validateMonthlyPlan({schemaVersion:1,month,revision:0,updatedAt:'',incomeQ1Cents:null,incomeQ2Cents:null,allocationQ1Cents:null,allocationQ2Cents:null,entries:[]});
}
export function summarizeMonthlyPlan(plan: MonthlyPlan) {
  validateMonthlyPlan(plan);
  const income = monthlyIncomeRows(plan).reduce((sum,row)=>sum+(row.amountCents??0),0);
  const committed = plan.entries.reduce((sum,entry) => sum + (entry.amountCents ?? 0),0);
  const free = income - committed;
  const positiveFree = Math.max(0,free);
  const q1 = plan.allocationQ1Cents ?? (plan.allocationQ2Cents === null ? Math.floor(positiveFree/2) : Math.max(0,positiveFree-plan.allocationQ2Cents));
  const q2 = plan.allocationQ2Cents ?? Math.max(0,positiveFree-q1);
  const missing = monthlyIncomeRows(plan).filter(row=>row.amountCents===null).length + plan.entries.filter(entry=>entry.amountCents===null).length;
  return {income,committed,free,q1,q2,missing,unassigned:free-q1-q2,ready:missing===0 && free>=0 && q1+q2<=free};
}
/** Plans follow income dates: September 15 funds Sep 15–29; September 30 funds Sep 30–Oct 14. */
export function monthlyTargetForPeriod(endDate: string): {month: string; half: 1|2} | null {
  if (!/^20\d{2}-\d{2}-\d{2}$/.test(endDate)) return null;
  const date = new Date(`${endDate}T12:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0,10)!==endDate) return null;
  if(date.getUTCDate()<=14){
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth()-1);
    const month=date.toISOString().slice(0,7);
    return validMonth(month)?{month,half:2}:null;
  }
  return {month:endDate.slice(0,7),half:1};
}
export function plannedPeriodBudget(plan: MonthlyPlan | null, endDate: string): number | null {
  const target=monthlyTargetForPeriod(endDate);
  if(!plan || !target || plan.month!==target.month) return null;
  const sum=summarizeMonthlyPlan(plan);
  return sum.ready ? target.half===1?sum.q1:sum.q2 : null;
}
export function compareCurrentPeriod(plan: MonthlyPlan,snapshot: FinancialSnapshot) {
  const target=monthlyTargetForPeriod(snapshot.periodEnd ?? '');
  if(!target || target.month!==plan.month) return null;
  const summary=summarizeMonthlyPlan(plan);
  const proposed=target.half===1?summary.q1:summary.q2;
  return {proposed,assigned:snapshot.totalBudgetCents,difference:proposed-snapshot.totalBudgetCents,half:target.half};
}

/** Tracking only: settled commitments remain part of the monthly allocation. */
export function setMonthlyEntrySettled(plan: MonthlyPlan,id: string,settled: boolean): MonthlyPlan {
  validateMonthlyPlan(plan);
  if(!plan.entries.some(entry=>entry.id===id))throw new Error('No encontré ese compromiso. Vuelve a abrir la planeación.');
  return validateMonthlyPlan({...plan,entries:plan.entries.map(entry=>entry.id===id?{...entry,settled}:entry)});
}
export function copyMonthlyPlanForMonth(previous: MonthlyPlan,current: MonthlyPlan): MonthlyPlan {
  validateMonthlyPlan(previous);validateMonthlyPlan(current);
  return validateMonthlyPlan({...previous,month:current.month,revision:current.revision,updatedAt:current.updatedAt,entries:previous.entries.map(entry=>({...entry,settled:false})),...(previous.incomes?{incomes:previous.incomes.map(row=>({...row,registeredInFunds:false}))}:{})});
}

/** Legacy aggregate amounts remain intact until the owner chooses to edit them. */
export function monthlyIncomeRows(plan: MonthlyPlan): MonthlyIncome[] {
  return plan.incomes ?? ([1,2] as const).map(half=>({id:`legacy_income_${half}`,name:'Ingreso previsto',half,amountCents:half===1?plan.incomeQ1Cents:plan.incomeQ2Cents,registeredInFunds:false}));
}
export function withMonthlyIncomes(plan: MonthlyPlan,incomes: MonthlyIncome[]): MonthlyPlan {
  const total=(half:1|2)=>{const rows=incomes.filter(row=>row.half===half);return rows.some(row=>row.amountCents===null)?null:rows.reduce((sum,row)=>sum+(row.amountCents??0),0);};
  return validateMonthlyPlan({...plan,incomes,incomeQ1Cents:total(1),incomeQ2Cents:total(2)});
}
export function setMonthlyIncomeRegistered(plan: MonthlyPlan,id: string,registeredInFunds: boolean): MonthlyPlan {
  validateMonthlyPlan(plan);
  const rows=monthlyIncomeRows(plan);
  if(!rows.some(row=>row.id===id))throw new Error('No encontré ese ingreso. Vuelve a abrir la planeación.');
  return withMonthlyIncomes(plan,rows.map(row=>row.id===id?{...row,registeredInFunds}:row));
}
